import { useEffect, useState } from "react";
import { Modal, Button, Table, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faEye,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../providers/authprovider";
import useAlert from "../../components/CustomAlert";

const CHANGE_TYPE_PL = {
  seed: "Import",
  create: "Utworzenie",
  edit: "Edycja",
  version_bump: "Zmiana wersji szablonu",
  restore: "Przywrócenie",
};

export default function ProtocolVersionsModal({
  show,
  protocolId,
  onClose,
  onRestored,
}) {
  const {
    protocols: { getVersions, getVersion, display },
    viewPdf,
  } = useAuth();
  const { showAlert, hideAlert, alert } = useAlert();
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [previewingRev, setPreviewingRev] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (!show || !protocolId) return;

    setRestoreTarget(null);
    hideAlert();
    setLoading(true);

    getVersions(protocolId, ({ success, content, error }) => {
      setLoading(false);
      if (!success) return showAlert(error);
      setVersions(content || []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, protocolId]);

  const handlePreview = (revision) => {
    hideAlert();
    setPreviewingRev(revision);

    getVersion(protocolId, revision, ({ success, content, error }) => {
      if (!success) {
        setPreviewingRev(null);
        return showAlert(error);
      }

      display(content, ({ success, content: blob, error }) => {
        setPreviewingRev(null);
        if (!success) return showAlert(error);
        viewPdf(blob);
      });
    });
  };

  const handleRestore = () => {
    if (!restoreTarget) return;

    const revision = restoreTarget.revision;

    setRestoring(true);
    hideAlert();

    getVersion(protocolId, revision, ({ success, content, error }) => {
      setRestoring(false);
      if (!success) return showAlert(error);

      setRestoreTarget(null);
      if (onRestored) onRestored(revision, content);
    });
  };

  const formatVersion = (version) =>
    Array.isArray(version) ? version.join(" ") : version;

  return (
    <Modal
      show={show}
      onHide={restoring ? undefined : onClose}
      size="lg"
      centered
      backdrop={restoring ? "static" : true}
    >
      <Modal.Header closeButton={!restoring}>
        <Modal.Title>Historia wersji protokołu</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {alert()}

        {loading && (
          <div className="text-center p-3">
            <FontAwesomeIcon icon={faSpinner} spin /> Wczytywanie...
          </div>
        )}

        {!loading && versions.length === 0 && (
          <p className="text-muted mb-0">Brak zapisanych wersji.</p>
        )}

        {!loading && versions.length > 0 && (
          <Table bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Data</th>
                <th>Autor</th>
                <th>Typ zmiany</th>
                <th>Wersja</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id}>
                  <td>{v.revision}</td>
                  <td>
                    {v.created_at
                      ? new Date(v.created_at).toLocaleString()
                      : ""}
                  </td>
                  <td>{v.userName}</td>
                  <td>{CHANGE_TYPE_PL[v.change_type] || v.change_type}</td>
                  <td>{formatVersion(v.version)}</td>
                  <td className="text-nowrap text-end">
                    <Button
                      size="sm"
                      variant="outline-info"
                      className="me-2"
                      disabled={previewingRev === v.revision}
                      onClick={() => handlePreview(v.revision)}
                    >
                      {previewingRev === v.revision ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faEye} /> Podgląd
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-warning"
                      disabled={restoring}
                      onClick={() => setRestoreTarget(v)}
                    >
                      <FontAwesomeIcon icon={faRotateLeft} /> Przywróć
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {restoreTarget && (
          <Alert variant="warning" className="mt-3 mb-0">
            <p className="mb-2">
              Załadować wersję <strong>#{restoreTarget.revision}</strong> do
              formularza? Bieżące dane zostaną zastąpione w edytorze. Zmiany
              utrwalisz dopiero przyciskiem <strong>Zapisz</strong> — wtedy
              powstanie nowa rewizja.
            </p>
            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                size="sm"
                className="me-2"
                disabled={restoring}
                onClick={() => setRestoreTarget(null)}
              >
                Anuluj
              </Button>
              <Button
                variant="warning"
                size="sm"
                disabled={restoring}
                onClick={handleRestore}
              >
                {restoring ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Wczytywanie...
                  </>
                ) : (
                  "Załaduj do formularza"
                )}
              </Button>
            </div>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={restoring} onClick={onClose}>
          Zamknij
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
